export interface BudgetVsActual {
  /** actualCost / budgetedCost * 100, rounded. */
  percent: number;
  /** budgetedCost - actualCost: positive is under budget, negative is over. */
  delta: number;
}

// Mirrors the reporting-store backend's budgetSpentPercentage (LoopFinancialsService.kt): a zero
// budget with any actual cost reads as fully spent (100%) rather than an undefined percentage.
export const getBudgetVsActual = (
  actualCost: number | null,
  budgetedCost: number | null,
): BudgetVsActual | null => {
  if (actualCost == null || budgetedCost == null) return null;

  let percent: number;
  if (budgetedCost > 0) {
    percent = Math.round((actualCost / budgetedCost) * 100);
  } else {
    percent = actualCost > 0 ? 100 : 0;
  }

  return { percent, delta: budgetedCost - actualCost };
};

// A positive delta (under budget / under the expected pace) reads as success, negative as error —
// shared so every under/over-framed figure in this feature agrees on which color means what.
export const getDeltaColorClass = (delta: number): string =>
  delta >= 0 ? 'text-text-success-bold' : 'text-text-error-bold';

// Same sign convention as getDeltaColorClass, for a Progress bar's indicatorClassName instead of text.
export const getDeltaIndicatorClass = (delta: number): string =>
  delta >= 0 ? 'bg-bg-success' : 'bg-bg-error';

// Same sign convention as getDeltaColorClass, for a sui-badge variant instead of a text/bg class.
export const getPaceVariant = (delta: number): 'success' | 'error' =>
  delta >= 0 ? 'success' : 'error';

export interface Pace {
  scheduleElapsedPercent: number;
  budgetVsActual: BudgetVsActual;
  expectedSpendToDate: number;
  actualCost: number;
  straightLineDelta: number;
}

// Straight-line pace: a budget/actual comparison against schedule progress, not just percent-spent.
export const getPace = (
  budgetedCost: number | null,
  actualCost: number | null,
  scheduleElapsedPercent: number | null,
): Pace | null => {
  const budgetVsActual = getBudgetVsActual(actualCost, budgetedCost);
  if (
    budgetedCost == null ||
    actualCost == null ||
    scheduleElapsedPercent == null ||
    !budgetVsActual
  ) {
    return null;
  }

  const expectedSpendToDate = Math.round(budgetedCost * (scheduleElapsedPercent / 100));
  return {
    scheduleElapsedPercent,
    budgetVsActual,
    expectedSpendToDate,
    actualCost,
    straightLineDelta: expectedSpendToDate - actualCost,
  };
};
